import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	output,
	signal,
	viewChild,
	ElementRef,
} from '@angular/core';
import { ESigningBlockType } from '@models/e-signing/enums';
import { IconsModule } from '@ui/icons/icons.module';
import { AVATAR_VARIANT } from '@ui/utils/constants';

@Component({
	selector: 'app-e-signing-canvas-block',
	standalone: true,
	imports: [IconsModule, CommonModule],
	templateUrl: './e-signing-canvas-block.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'w-full h-full' },
})
export class ESigningCanvasBlockComponent {
	readonly ESigningBlockType = ESigningBlockType;
	textDefaultValue = input<string>('');
	textDefaultValueChange = output<string>();
	blockFocus = output();
	blockBlur = output();
	isEditingChanged = output<boolean>();
	isEditing = signal(false);
	private editValue_ = signal('');
	private textInput_ = viewChild<ElementRef<HTMLTextAreaElement>>('textInput');
	private buttonElement_ =
		viewChild<ElementRef<HTMLButtonElement>>('blockButton');
	variant = input<AVATAR_VARIANT>(AVATAR_VARIANT.Emerald);
	disabled = input<boolean>(false);
	blockType = input.required<ESigningBlockType>();
	size = input<'sm' | 'md' | 'lg'>('sm');
	clicked = output<MouseEvent>();
	classes = computed(() => [
		this.getColorVariant(),
		this.getSizeVariant(),
		this.getDragVariant(),
		this.getMinWidthVariant(),
		this.getBlockTypeVariant(),
		this.getAlignmentVariant(),
	]);
	iconClasses = computed(() => this.getIconSizeVariant());
	draggable = input<boolean>(false);

	private eSigningBlockButtonTypes = {
		[ESigningBlockType.SIGN]: {
			label: 'Sign',
			iconName: 'signature',
		},
		[ESigningBlockType.INITIALS]: {
			label: 'Initial',
			iconName: 'scribble',
		},
		[ESigningBlockType.CHECKBOX]: {
			label: '',
			iconName: 'square',
		},
		[ESigningBlockType.DATE]: {
			label: 'MM/DD/YYYY',
			iconName: 'calendar',
		},
		[ESigningBlockType.TEXT]: {
			label: 'Enter Text',
			iconName: '',
		},
	};

	private readonly BASE_BG_AND_HOVER_CLASSES = {
		[AVATAR_VARIANT.Emerald]: 'bg-emerald-100 hover:bg-emerald-200',
		[AVATAR_VARIANT.Lime]: 'bg-lime-100 hover:bg-lime-200',
		[AVATAR_VARIANT.Indigo]: 'bg-indigo-100 hover:bg-indigo-200',
		[AVATAR_VARIANT.Orange]: 'bg-orange-100 hover:bg-orange-200',
		[AVATAR_VARIANT.Purple]: 'bg-purple-100 hover:bg-purple-200',
		[AVATAR_VARIANT.Pink]: 'bg-pink-100 hover:bg-pink-200',
	} as const;

	private readonly FOCUS_CLASSES = {
		[AVATAR_VARIANT.Emerald]:
			'focus-within:border-emerald-400 focus:bg-emerald-200 focus:border-emerald-400',
		[AVATAR_VARIANT.Lime]:
			'focus-within:border-lime-400 focus:bg-lime-200 focus:border-lime-400',
		[AVATAR_VARIANT.Indigo]:
			'focus-within:border-indigo-400 focus:bg-indigo-200 focus:border-indigo-400',
		[AVATAR_VARIANT.Orange]:
			'focus-within:border-orange-400 focus:bg-orange-200 focus:border-orange-400',
		[AVATAR_VARIANT.Purple]:
			'focus-within:border-purple-400 focus:bg-purple-200 focus:border-purple-400',
		[AVATAR_VARIANT.Pink]:
			'focus-within:border-pink-400 focus:bg-pink-200 focus:border-pink-400',
	} as const;

	private readonly ACTIVE_BORDER_CLASSES = {
		[AVATAR_VARIANT.Emerald]: 'active:border-emerald-400',
		[AVATAR_VARIANT.Lime]: 'active:border-lime-400',
		[AVATAR_VARIANT.Indigo]: 'active:border-indigo-400',
		[AVATAR_VARIANT.Orange]: 'active:border-orange-400',
		[AVATAR_VARIANT.Purple]: 'active:border-purple-400',
		[AVATAR_VARIANT.Pink]: 'active:border-pink-400',
	} as const;

	public blockInfo_ = computed(() => {
		return this.eSigningBlockButtonTypes[this.blockType()];
	});

	private backgroundClasses_ = computed(() => {
		if (this.isEditing()) {
			return 'bg-white';
		}
		return this.BASE_BG_AND_HOVER_CLASSES[this.variant()];
	});

	private activeBorderClasses_ = computed(() => {
		if (this.disabled()) return '';
		return this.ACTIVE_BORDER_CLASSES[this.variant()];
	});

	private focusClasses_ = computed(() => {
		if (this.disabled()) return '';
		return this.FOCUS_CLASSES[this.variant()];
	});

	private getBlockTypeVariant = computed(() => {
		return this.blockType() === ESigningBlockType.CHECKBOX ? 'p-px' : 'p-1';
	});

	private getDragVariant = computed(() => {
		return this.draggable() ? 'cursor-move' : '';
	});

	private getSizeVariant = computed(() => {
		return (
			this.blockInfo_().iconName
				? {
						sm: 'pr-3 text-xs',
						md: 'pr-4 text-sm',
						lg: 'pr-4 text-base',
					}
				: {
						sm: 'text-xs',
						md: 'text-sm',
						lg: 'text-base',
					}
		)[this.size()];
	});

	private getColorVariant = computed(() => {
		return [
			this.backgroundClasses_(),
			this.activeBorderClasses_(),
			this.focusClasses_(),
		].join(' ');
	});

	private getMinWidthVariant = computed(() => {
		return this.blockInfo_().iconName
			? {
					sm: 'min-w-9',
					md: 'min-w-10',
					lg: 'min-w-10',
				}[this.size()]
			: '';
	});

	private getIconSizeVariant = computed(() => {
		return {
			sm: 'min-w-3.5 min-h-3.5 h-3.5 w-3.5',
			md: 'min-w-4 min-h-4 h-4 w-4',
			lg: 'min-w-4 min-h-4 h-4 w-4',
		}[this.size()];
	});

	private getAlignmentVariant = computed(() => {
		return this.textDefaultValue() ? 'items-start' : 'items-center';
	});

	onBlockClick(event: MouseEvent) {
		if (this.blockType() !== ESigningBlockType.TEXT) {
			this.clicked.emit(event);
		}
	}

	onBlockDoubleClick(event: MouseEvent) {
		if (
			this.blockType() === ESigningBlockType.TEXT &&
			!this.isEditing() &&
			!this.disabled()
		) {
			event.preventDefault();
			event.stopPropagation();
			this.enterEditMode();
		}
	}

	onKeyDown(event: KeyboardEvent) {
		if (
			this.blockType() === ESigningBlockType.TEXT &&
			!this.isEditing() &&
			!this.disabled() &&
			(event.key === 'Enter' ||
				event.key === ' ' ||
				/^[a-zA-Z0-9]$/.test(event.key))
		) {
			const buttonElement = this.buttonElement_()?.nativeElement;
			const isFocused = buttonElement === document.activeElement;

			if (isFocused) {
				event.preventDefault();
				event.stopPropagation();
				this.enterEditMode();
			}
		}
	}

	private enterEditMode() {
		this.editValue_.set(this.textDefaultValue());
		this.isEditingChanged.emit(true);
		this.isEditing.set(true);
		setTimeout(() => {
			const textInput = this.textInput_();
			if (textInput?.nativeElement) {
				textInput.nativeElement.focus();
				textInput.nativeElement.select();
			}
		}, 0);
	}

	onInputKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			this.exitEditMode(false);
		}
	}

	onInputBlur() {
		this.exitEditMode(true);
	}

	private exitEditMode(save: boolean) {
		if (save && this.editValue_() !== this.textDefaultValue()) {
			this.textDefaultValueChange.emit(this.editValue_());
		}
		this.isEditing.set(false);
		this.isEditingChanged.emit(false);
	}

	onInputChange(event: Event) {
		const target = event.target as HTMLTextAreaElement;
		this.editValue_.set(target.value);
	}
}
